"use client"

import { Calendar, MapPin, Users, Anchor, ExternalLink, ChevronUp, ChevronDown } from "lucide-react"
import { Link } from "react-router-dom"
import { useEffect, useState, useRef } from "react"

const Hero = () => {
  const [scrolled, setScrolled] = useState(false)
  const [topBannerVisible, setTopBannerVisible] = useState(true)
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0)
  const [isSponsorsExpanded, setIsSponsorsExpanded] = useState(false)
  const videoRef = useRef(null)

  // Custom sponsors array
  const sponsors = [
    {
      id: 1,
      name: "Ethiopian Airlines",
      logo: "/assets/images/airliness.png",
      category: "Aviation",
      description: "Connecting Africa to the world with excellence in aviation services.",
      color: "from-blue-600 to-blue-800",
      website: "https://www.ethiopianairlines.com/",
    },
    {
      id: 2,
      name: "Ethiopian Maritime Authority",
      logo: "/assets/images/ema.png",
      category: "Maritime",
      description: "Regulating and promoting Ethiopia's maritime industry development.",
      color: "from-green-600 to-emerald-700",
      website: "https://etmaritime.com/",
    },
    {
      id: 3,
      name: "EMA Marine ",
      logo: "/assets/images/EMA_logo.png",
      category: "Marine Services",
      description: "Providing comprehensive maritime services and crew management.",
      color: "from-red-600 to-red-800",
      website: "https://www.emacrew.com/",
    },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.3
    }
  }, [])

  // Auto-rotate sponsors
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSponsorIndex((prev) => (prev + 1) % sponsors.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [sponsors.length])

  const nextSponsor = () => {
    setCurrentSponsorIndex((prev) => (prev + 1) % sponsors.length)
  }

  const prevSponsor = () => {
    setCurrentSponsorIndex((prev) => (prev - 1 + sponsors.length) % sponsors.length)
  }

  const currentSponsor = sponsors[currentSponsorIndex]
  const visibleSponsors = isSponsorsExpanded ? sponsors : sponsors.slice(0, 3)

  return (
    <>
      <section
        id="home"
        className={`relative h-screen w-full flex items-center justify-center overflow-hidden transition-all duration-300
        }`}
      >
        {/* Image Background */}
        <div className="absolute inset-0 z-0 w-full h-screen">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/assets/12008779_3840_2160_30fps.mp4"
            poster="/assets/images/freight-shipping.png"
            autoPlay
            muted
            playsInline
            loop
            ref={videoRef}
            onLoadedData={(e) => {
              e.target.playbackRate = 0.5
              const playPromise = e.target.play()
              if (playPromise !== undefined) {
                playPromise.catch((error) => {
                  console.log("Autoplay prevented:", error)
                })
              }
            }}
          ></video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-primary-navy/70"></div>
        </div>

        {/* Left Sponsors Section */}
        <div className="absolute top-[13%] right-6 w-[13.75rem] flex-wrap z-30 h-full hidden xl:block">
          <div className="backdrop-blur-md w-55 min-h-[45%] bg-white/10 p-6 rounded-xl border border-white/20 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="text-xs text-white/70 mb-2">Official Sponsors</div>
              <div className="h-0.5 bg-gradient-to-r from-transparent via-primary-orange to-transparent"></div>
            </div>

            {/* Featured Sponsor Carousel */}
            <div className="mb-6 w-full">
              <div className="relative group">
                <div
                  className={`bg-gradient-to-br ${currentSponsor.color} p-4 rounded-lg shadow-lg transform transition-all duration-500 hover:scale-105`}
                >
                  <div className="flex items-center w-50 justify-between mb-3">
                    <img
                      src={currentSponsor.logo || "/assets/images/tech-stack-react.png"}
                      alt={currentSponsor.name}
                      className="h-12 w-20 object-contain bg-white/95 rounded px-3 py-2 shadow-md"
                    />
                    <a
                      href={currentSponsor.website}
                      className="text-white/80 hover:text-white transition-colors bg-white/20 rounded-full p-2 hover:bg-white/30"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-1">{currentSponsor.name}</h3>
                  <p className="text-white/80 text-xs mb-2">{currentSponsor.category}</p>
                  <p className="text-white/70 text-xs">{currentSponsor.description}</p>
                </div>

                {/* Navigation buttons */}
                <button
                  onClick={prevSponsor}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
                >
                  <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                </button>
                <button
                  onClick={nextSponsor}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
                >
                  <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                </button>
              </div>

              {/* Sponsor indicators */}
              <div className="flex justify-center gap-1 mt-3">
                {sponsors.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSponsorIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSponsorIndex ? "bg-primary-orange scale-125" : "bg-white/30 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Animated wave effect at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-r from-primary-navy/30 to-primary-green/30 backdrop-blur-sm
                      transform -skew-y-2 z-10"
        ></div>

        {/* Floating anchor icon */}
        <div
          className={`absolute hidden sm:block bottom-40 left-20 transition-all duration-700 ${
            scrolled ? "opacity-0 -translate-y-10" : "opacity-100"
          }`}
        >
          <Anchor className="h-16 w-16 text-primary-orange/80 animate-pulse" />
        </div>

        {/* Content */}
        <div
          className="relative z-20 text-center text-white px-4 max-w-5xl mx-auto
                      transform transition-all duration-500 hover:scale-[1.02]"
        >
          {/* Subtle Heading with animated underline */}
          <div className="relative inline-block">
            <p className="text-xl md:text-2xl text-white font-light mb-4 tracking-wider"></p>
            <div
              className="absolute -bottom-1 left-1/4 right-1/4 h-0.5 bg-primary-orange
                          animate-pulse"
            ></div>
          </div>
          <div className="flex flex-col gap-5 pb-5">
            {/* Conference Name with gradient text */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-4 tracking-tight">
              <span className="text-white drop-shadow-lg text-4xl md:text-5xl py-2 lg:text-7xl">Africa Maritime</span>
            </h1>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="">Conference </span>
            </h2>
            <h3 className="text-2xl md:text-4xl lg:text-4xl font-bold text-white drop-shadow-xl leading-tight tracking-tight">
              Navigating The Future Of Africa's Blue Economy
            </h3>
          </div>

          {/* Event detail section with glass morphism */}
          <div
            className="flex flex-col items-start text-sm sm:text-base md:flex-row sm:items-center justify-center gap-3 sm:gap-10 mb-12 
                          backdrop-blur-md bg-white/10 p-6 rounded-xl border border-white/20
                          shadow-2xl"
          >
            {/* Date */}
            <div className="flex items-center gap-3 group">
              <Calendar className="h-6 w-6 text-primary-orange group-hover:scale-110 transition-transform" />
              <span className="text-white text-lg">Coming soon</span>
            </div>

            {/* Venue */}
            <div className="flex items-center gap-3 group">
              <MapPin className="h-6 w-6 text-primary-orange group-hover:scale-110 transition-transform" />
              <span className="text-white text-lg">Addis Ababa, Ethiopia</span>
            </div>

            {/* Attendees */}
            <div className="flex items-center gap-3 group">
              <Users className="h-6 w-6 text-primary-orange group-hover:scale-110 transition-transform" />
              <span className="text-white text-lg">500+ Maritime Professionals</span>
            </div>
          </div>

          {/* CTA Button with animated gradient border */}
          <div className="relative group inline-block max-w-[220px] mx-auto">
            <div
              className="absolute -inset-0.5 bg-gradient-to-r from-primary-orange to-primary-blue 
                          rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-500"
            ></div>
            
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden sm:block absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="animate-bounce">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white/80"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m0 0l-6-6m6 6l6-6" />
            </svg>
          </div>
        </div>
      </section>
    </>
  )
}

export default Hero
